<template>
  <div class="flex flex-col gap-5">
    <section v-if="showMode" class="flex flex-col gap-2" aria-labelledby="player-filter-mode-label">
      <div>
        <h3 id="player-filter-mode-label" class="text-sm font-semibold text-text-primary">{{ messages.controls.modeHeading }}</h3>
        <p class="text-xs text-text-muted">{{ messages.controls.modeDescription }}</p>
      </div>
      <div class="grid grid-cols-2 gap-2 rounded-lg border border-border bg-bg-surface p-1" role="group" :aria-label="messages.controls.modeGroupLabel">
        <button
          type="button"
          class="rounded-md px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :class="mode === PlayerFilterMode.Filter ? 'bg-accent text-bg shadow-sm' : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'"
          :aria-pressed="mode === PlayerFilterMode.Filter ? 'true' : 'false'"
          @click="emit('update:mode', PlayerFilterMode.Filter)"
        >
          {{ messages.controls.modeFilter }}
        </button>
        <button
          type="button"
          class="rounded-md px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :class="mode === PlayerFilterMode.Highlight ? 'bg-accent text-bg shadow-sm' : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'"
          :aria-pressed="mode === PlayerFilterMode.Highlight ? 'true' : 'false'"
          @click="emit('update:mode', PlayerFilterMode.Highlight)"
        >
          {{ messages.controls.modeHighlight }}
        </button>
      </div>
    </section>

    <section class="flex flex-col gap-2" aria-labelledby="player-name-filter-label">
      <div>
        <h3 id="player-name-filter-label" class="text-sm font-semibold text-text-primary">{{ messages.controls.nameHeading }}</h3>
        <p class="text-xs text-text-muted">{{ messages.controls.nameDescription }}</p>
      </div>
      <AppInput
        :model-value="filters.name"
        :input-aria-label="messages.controls.nameInputLabel"
        type="text"
        name="player-filter-name"
        :placeholder="messages.controls.nameInputPlaceholder"
        @update:model-value="updateName"
      />
    </section>

    <section class="flex flex-col gap-2" aria-labelledby="player-weapon-filter-label">
      <div>
        <h3 id="player-weapon-filter-label" class="text-sm font-semibold text-text-primary">{{ messages.controls.weaponsHeading }}</h3>
        <p class="text-xs text-text-muted">
          {{ messages.controls.weaponsDescription }}
        </p>
      </div>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        <button
          v-for="weapon in WEAPONS"
          :key="weapon.slug"
          type="button"
          class="flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs font-semibold transition-[border-color,background-color,color,box-shadow] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :class="filters.weaponSlugs.includes(weapon.slug) ? 'border-accent bg-accent/10 text-text-primary shadow-sm' : 'border-border bg-bg-surface text-text-secondary hover:border-border-strong hover:bg-bg-elevated hover:text-text-primary'"
          :aria-pressed="filters.weaponSlugs.includes(weapon.slug) ? 'true' : 'false'"
          @click="toggleWeapon(weapon.slug)"
        >
          <img :src="weapon.icon" alt="" class="size-6 shrink-0 rounded-sm object-cover" aria-hidden="true" />
          <span class="min-w-0 truncate">{{ weapon.name }}</span>
        </button>
      </div>
    </section>

    <section class="flex flex-col gap-2" aria-labelledby="player-tag-filter-label">
      <div>
        <h3 id="player-tag-filter-label" class="text-sm font-semibold text-text-primary">{{ messages.controls.tagsHeading }}</h3>
        <p class="text-xs text-text-muted">{{ messages.controls.tagsDescription }}</p>
      </div>
      <div v-if="tags.length > 0" class="flex flex-wrap gap-2">
        <button
          v-for="tag in tags"
          :key="tag.id"
          type="button"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold shadow-sm transition-[background-color,border-color,color,box-shadow] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :class="tagChipClassFor({ color: tag.color, active: filters.tagIds.includes(tag.id) })"
          :aria-pressed="filters.tagIds.includes(tag.id) ? 'true' : 'false'"
          @click="toggleTag(tag.id)"
        >
          <span class="size-1.5 rounded-full" :class="tagSwatchClassFor(tag.color)" aria-hidden="true" />
          {{ tagDisplayName(tag.name) }}
        </button>
      </div>
      <p v-else class="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
        {{ messages.controls.noGuildTags }}
      </p>
    </section>

    <div v-if="filtersActive" class="flex justify-end border-t border-border pt-4">
      <AppButton :variant="ButtonVariant.Ghost" :size="ButtonSize.Sm" @click="clearFilters">
        {{ messages.controls.clearFilters }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS } from '@/data/weapons'
import { tagChipClassFor, tagDisplayName, tagSwatchClassFor } from '@/features/gvg-planner/composables'
import { AppButton, AppInput, ButtonSize, ButtonVariant } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import {
  emptyPlayerFilters,
  PlayerFilterMode,
  playerFiltersAreActive,
} from '../../lib/playerFilters'
import type {
  PlayerFilterMode as PlayerFilterModeType,
  PlayerFilters,
} from '../../lib/playerFilters'
import type { PlayerTag } from '../../types'
import { playerFiltersEn } from './locales/en.ts'
import { playerFiltersRu } from './locales/ru.ts'

export interface PlayerFilterControlsProps {
  readonly filters: PlayerFilters
  readonly tags: ReadonlyArray<PlayerTag>
  readonly mode?: PlayerFilterModeType
  readonly showMode?: boolean
}

const props = withDefaults(defineProps<PlayerFilterControlsProps>(), {
  mode: PlayerFilterMode.Filter,
  showMode: false,
})

const emit = defineEmits<{
  (event: 'update:filters', filters: PlayerFilters): void
  (event: 'update:mode', mode: PlayerFilterModeType): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: playerFiltersEn, ru: playerFiltersRu }))
const filtersActive = computed<boolean>(() => playerFiltersAreActive(props.filters))

function updateName(name: string): void {
  emit('update:filters', { ...props.filters, name })
}

function toggleWeapon(slug: string): void {
  emit('update:filters', {
    ...props.filters,
    weaponSlugs: props.filters.weaponSlugs.includes(slug)
      ? props.filters.weaponSlugs.filter((candidate) => candidate !== slug)
      : [...props.filters.weaponSlugs, slug],
  })
}

function toggleTag(tagId: string): void {
  emit('update:filters', {
    ...props.filters,
    tagIds: props.filters.tagIds.includes(tagId)
      ? props.filters.tagIds.filter((candidate) => candidate !== tagId)
      : [...props.filters.tagIds, tagId],
  })
}

function clearFilters(): void {
  emit('update:filters', emptyPlayerFilters())
}
</script>